/**
 * ChallengeSheet.tsx — 2004va
 *
 * Shown on QueueItemCard when grade is C or D.
 * Creator types context, system re-evaluates with it.
 * AI either accepts the challenge (revises grade) or holds its verdict.
 *
 * Design: inline expansion, not a modal — stays in the card flow.
 */

import { useState } from 'react'
import { MessageSquare, Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { submitCreatorChallenge } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore } from '@/stores/sessionStore'
import { cn } from '@/components/ui/utils'

interface Props {
  mediaItemId:   string
  cloudinaryUrl: string
  mediaType:     string
  fileName:      string
  eventName?:    string
  originalGrade: string
  originalScore: number
  originalNote:  string
  onRevised: (revised: {
    grade: string
    score: number
    note:  string
    accepted: boolean
  }) => void
}

type State = 'idle' | 'open' | 'submitting' | 'accepted' | 'rejected'

export function ChallengeSheet({
  mediaItemId, cloudinaryUrl, mediaType, fileName,
  eventName, originalGrade, originalScore, originalNote,
  onRevised,
}: Props) {
  const { participant }       = useAuthStore()
  const { session }           = useSessionStore()
  const [state, setState]     = useState<State>('idle')
  const [note, setNote]       = useState('')
  const [result, setResult]   = useState<{ grade: string; score: number; note: string; accepted: boolean } | null>(null)
  const [error, setError]     = useState('')

  async function handleSubmit() {
    if (!note.trim() || !participant) return
    setState('submitting')
    setError('')

    try {
      const res = await submitCreatorChallenge({
        mediaItemId,
        participantId: participant.id,
        challengeNote: note.trim(),
        originalGrade,
        originalScore,
        originalNote,
        cloudinaryUrl,
        mediaType,
        fileName,
        eventName,
        sessionBrief: session?.sessionBrief ?? undefined,
      })

      setResult({ grade: res.revised_grade, score: res.revised_score, note: res.revised_note, accepted: res.challenge_accepted })
      setState(res.challenge_accepted ? 'accepted' : 'rejected')
      onRevised({ grade: res.revised_grade, score: res.revised_score, note: res.revised_note, accepted: res.challenge_accepted })
    } catch (e) {
      setError('Re-evaluation failed — try again')
      setState('open')
    }
  }

  // Only show challenge option for C and D grades
  if (!['C', 'D'].includes(originalGrade)) return null

  return (
    <div className="border-t border-room-border pt-3 mt-1">
      {/* ── Idle — show subtle prompt ── */}
      {state === 'idle' && (
        <button
          type="button"
          onClick={() => setState('open')}
          className="flex items-center gap-1.5 text-xs text-room-muted hover:text-white transition-colors"
        >
          <MessageSquare size={12} />
          Disagree with this verdict? Add context
          <ChevronDown size={11} className="ml-auto" />
        </button>
      )}

      {/* ── Open — text input ── */}
      {state === 'open' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <MessageSquare size={12} className="text-room-accent" />
              <span className="text-xs font-semibold text-white">Challenge this verdict</span>
            </div>
            <button
              type="button"
              onClick={() => { setState('idle'); setNote('') }}
              className="text-room-muted hover:text-white"
            >
              <ChevronUp size={11} />
            </button>
          </div>

          <p className="text-[11px] text-room-muted leading-relaxed">
            Tell the AI what it missed. Was this a fashion shoot? A personal project? 
            Context it didn't have when it graded this.
          </p>

          <textarea
            autoFocus
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={`e.g. "This is a fashion shoot for a brand client — the intent was to show the garment, not a dramatic moment. The model positioning is deliberate."`}
            rows={3}
            maxLength={400}
            className={cn(
              'w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5',
              'text-xs text-white placeholder:text-room-muted resize-none',
              'focus:outline-none focus:border-room-accent/50 transition-colors leading-relaxed'
            )}
          />

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-room-muted">{note.length}/400</span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!note.trim()}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                'bg-room-accent text-white hover:bg-room-accent/90',
                'disabled:opacity-40 disabled:cursor-not-allowed'
              )}
            >
              <MessageSquare size={11} />
              Submit challenge
            </button>
          </div>

          {error && <p className="text-xs text-room-error">{error}</p>}
        </div>
      )}

      {/* ── Submitting ── */}
      {state === 'submitting' && (
        <div className="flex items-center gap-2 py-1">
          <Loader2 size={13} className="animate-spin text-room-accent" />
          <span className="text-xs text-room-muted">Re-evaluating with your context…</span>
        </div>
      )}

      {/* ── Accepted — grade revised ── */}
      {state === 'accepted' && result && (
        <div className="space-y-2 bg-emerald-950/30 border border-emerald-800/30 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <CheckCircle size={13} className="text-emerald-400 flex-shrink-0" />
            <span className="text-xs font-semibold text-emerald-300">Verdict revised</span>
            <span className={cn(
              'ml-auto text-xs font-black px-1.5 py-0.5 rounded',
              result.grade === 'A' ? 'bg-emerald-900/60 text-emerald-300' :
              result.grade === 'B' ? 'bg-blue-900/60 text-blue-300' :
              'bg-amber-900/60 text-amber-300'
            )}>
              Grade {result.grade}
            </span>
          </div>
          <p className="text-[11px] text-emerald-300/80 italic leading-relaxed border-l-2 border-emerald-700/40 pl-2">
            {result.note}
          </p>
        </div>
      )}

      {/* ── Rejected — original verdict stands ── */}
      {state === 'rejected' && result && (
        <div className="space-y-2 bg-red-950/20 border border-red-800/20 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <XCircle size={13} className="text-red-400 flex-shrink-0" />
            <span className="text-xs font-semibold text-red-300">Original verdict stands</span>
            <span className="text-[10px] text-room-muted ml-auto">Grade {originalGrade}</span>
          </div>
          <p className="text-[11px] text-red-300/70 italic leading-relaxed border-l-2 border-red-700/30 pl-2">
            {result.note}
          </p>
        </div>
      )}
    </div>
  )
}
